/**
 * Product URL mapping system
 * Maps friendly URLs to internal product data
 */

export interface ProductMapping {
  slug: string;
  name: string;
  description: string;
  internalId: string;
  versions: VersionMapping[];
  latestVersion: string;
  thumbnailUrl?: string;
}

export interface VersionMapping {
  version: string; // e.g., 'v1', 'v2'
  internalVersion: string; // e.g., 'v1_2', 'v2_1'
  description?: string;
  releaseDate?: string;
}

// Central registry of all products and their URL mappings
export const PRODUCT_MAPPINGS: ProductMapping[] = [
  {
    slug: 'kibu-wired-headphones',
    name: 'Kibu Wired Headphones',
    description: 'Premium wired headphones with high-quality drivers and comfortable design. Build your own pair with our step-by-step video instructions.',
    internalId: 'kwh-01-01',
    latestVersion: 'v1',
    versions: [
      {
        version: 'v1',
        internalVersion: 'v1_2',
        description: 'Initial release with improved assembly instructions',
      }
    ],
    thumbnailUrl: '/assets/images/product_detail_cards/boy-building.webp'
  },
  // Future products can be added here:
  // {
  //   slug: 'kibu-bluetooth-headphones',
  //   name: 'Kibu Bluetooth Headphones',
  //   description: 'Wireless freedom with premium sound quality.',
  //   internalId: 'kbt-01-01',
  //   latestVersion: 'v1',
  //   versions: [
  //     {
  //       version: 'v1',
  //       internalVersion: 'v1_0',
  //     }
  //   ]
  // }
];

// Helper functions for URL mapping
export const getProductBySlug = (slug: string): ProductMapping | undefined => {
  return PRODUCT_MAPPINGS.find(product => product.slug === slug);
};

export const getProductByInternalId = (internalId: string): ProductMapping | undefined => {
  return PRODUCT_MAPPINGS.find(product => product.internalId === internalId);
};

export const getVersionInfo = (slug: string, version: string): VersionMapping | undefined => {
  const product = getProductBySlug(slug);
  return product?.versions.find(v => v.version === version);
};

export const getInternalProductId = (slug: string, version?: string): string | undefined => {
  const product = getProductBySlug(slug);
  if (!product) return undefined;

  const targetVersion = version || product.latestVersion;
  const versionInfo = product.versions.find(v => v.version === targetVersion);

  return versionInfo ? product.internalId : undefined;
};

export const getAllProducts = (): ProductMapping[] => {
  return PRODUCT_MAPPINGS;
};

export const parseUrl = (path: string) => {
  // Remove leading slash and split
  const parts = path.replace(/^\//, '').split('/');

  if (parts.length === 0 || parts[0] === '') {
    return { type: 'landing' as const };
  }

  const productSlug = parts[0];
  const versionPart = parts[1];

  // Check if it's a valid product
  const product = getProductBySlug(productSlug);
  if (!product) {
    return { type: 'not-found' as const, path };
  }

  if (!versionPart) {
    // Product without version - redirect to latest
    return {
      type: 'redirect' as const,
      redirectTo: `/${productSlug}/${product.latestVersion}`,
      productSlug
    };
  }

  // Check if version exists
  const versionExists = product.versions.some(v => v.version === versionPart);
  if (!versionExists) {
    return { type: 'not-found' as const, path };
  }

  return {
    type: 'product' as const,
    productSlug,
    version: versionPart,
    internalId: product.internalId
  };
};